import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import LockedJob from '@components/icons/LockedJob.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { MeResponseDtoOrganizationWorkspace, UpdateNotificationSettingsBodyDto } from 'app/API';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { usePageContext } from '../../providers/PageProvider';
import { JobNameField } from './styles/JobNameField';
import { MainContent } from './styles/MainContent';
import { StyledLink } from './styles/StyledLink';

type TData = { type: string; name: string };
type SettingsKey = keyof UpdateNotificationSettingsBodyDto;

export const Content = () => {
  const { me } = useAuthContext();
  const {
    isBusy,
    isUpdatingSettings,
    isRemovingSubscription,
    hasLoadError,
    retryFailedQueries,
    settings,
    updateSettings,
    subscriptions,
    deleteSubscription,
    jobSubscriptions,
    workspaceSubscriptions,
    unwatchJob,
    unwatchWorkspace,
  } = usePageContext();
  const [jobSubsDisclosureOpen, setJobSubsDisclosureOpen] = useState(false);
  const [workspaceSubsDisclosureOpen, setWorkspaceSubsDisclosureOpen] = useState(false);
  const [devicesDisclosureOpen, setDevicesDisclosureOpen] = useState(false);
  const navigate = useNavigate();

  const loadErrorBanner = hasLoadError ? (
    <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
      <span>Failed to load notification settings.</span>
      <TextButton type="button" onClick={retryFailedQueries}>
        Retry
      </TextButton>
    </div>
  ) : null;

  if (!settings) {
    return (
      <>
        <LinearProgressLoader active={isBusy} />
        <MainContent>{loadErrorBanner}</MainContent>
      </>
    );
  }

  const toggleSettings = (name: SettingsKey) => {
    updateSettings({ ...settings, [name]: !settings[name] });
  };

  const allWorkspaces: MeResponseDtoOrganizationWorkspace[] = [];
  me.organizations.forEach((org) => {
    org.workspaces.forEach((ws) => {
      allWorkspaces.push(ws);
    });
  });

  const jobClick = (workspaceId: string, id: string) => {
    const organization = me.organizations.find((org) => org.workspaces.find((ws) => ws.id === workspaceId));
    const workspace = (organization?.workspaces ?? []).find((ws) => workspaceId === ws.id);
    if (organization && workspace) {
      navigate(routes.jobDetails.make(organization.slug, workspace.slug, id));
    }
  };

  const workspaceClick = (id: string) => {
    const organization = me.organizations.find((org) => org.workspaces.find((ws) => ws.id === id));
    const workspace = (organization?.workspaces ?? []).find((ws) => id === ws.id);
    if (organization && workspace) {
      navigate(routes.workspaceDetails.make(organization?.slug ?? '', workspace?.slug ?? ''));
    }
  };

  const renderDevicesList = () => {
    if (!subscriptions.length) {
      return null;
    }
    return (
      <DisclosureSection
        title="Devices"
        open={devicesDisclosureOpen}
        setOpenState={setDevicesDisclosureOpen}
        loading={false}
      >
        <TableWrapper className="table">
          <Table>
            <Tbody>
              {subscriptions.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <div>
                      {data.browserName || data.id}
                      {data.browserVersion ? ` v${data.browserVersion}` : ''} (
                      {data.osName || data.deviceName || 'Unknown device'})
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <IconButtonDelete
                        aria-label="Remove device"
                        onClick={() => {
                          deleteSubscription(data.id);
                        }}
                        disabled={isRemovingSubscription}
                      >
                        <TrashBin />
                      </IconButtonDelete>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
      </DisclosureSection>
    );
  };

  const renderJobsList = () => {
    if (!jobSubscriptions.length) {
      return null;
    }
    return (
      <DisclosureSection
        title="Job subscriptions"
        open={jobSubsDisclosureOpen}
        setOpenState={setJobSubsDisclosureOpen}
        loading={false}
      >
        <TableWrapper className="table">
          <Table>
            <Tbody>
              {jobSubscriptions.map((data) => {
                const workspace = allWorkspaces.find((ws) => data.job.workspaceId === ws.id);
                let organization;
                if (workspace) {
                  organization = me.organizations.find((org) => workspace.organizationId === org.id);
                }
                return (
                  <Tr key={data.id}>
                    <Td>
                      <JobNameField>
                        <div className="flex gap-3 items-center">
                          {data.job.asOneLocked ? <LockedJob className="flex-shrink-0 pl-2" width="30" /> : null}
                          <div>
                            <div className="job-name">
                              {data.job.asOneLocked ? (
                                data.job.name
                              ) : (
                                <div className="flex flex-row">
                                  {data.job.meetingId ? <TagAccent className="mr-2">AS ONE</TagAccent> : null}
                                  <StyledLink
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      jobClick(data.job.workspaceId, data.jobId);
                                    }}
                                    to={routes.jobDetails.make(
                                      organization?.slug ?? '',
                                      workspace?.slug ?? '',
                                      data.jobId,
                                    )}
                                  >
                                    {data.job.name}
                                  </StyledLink>
                                </div>
                              )}
                            </div>
                            <div className="id-field">{data.id}</div>
                          </div>
                        </div>
                      </JobNameField>
                    </Td>
                    <Td>
                      <div>
                        <IconButtonDelete
                          aria-label="Unwatch job"
                          onClick={() => {
                            unwatchJob(data.jobId);
                          }}
                          disabled={isRemovingSubscription}
                        >
                          <TrashBin />
                        </IconButtonDelete>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
      </DisclosureSection>
    );
  };

  const renderWorkspacesList = () => {
    if (!workspaceSubscriptions.length) {
      return null;
    }
    return (
      <DisclosureSection
        title="Workspace subscriptions"
        open={workspaceSubsDisclosureOpen}
        setOpenState={setWorkspaceSubsDisclosureOpen}
        loading={false}
      >
        <TableWrapper className="table">
          <Table>
            <Tbody>
              {workspaceSubscriptions.map((data) => {
                const organization = me.organizations.find((org) => data.workspace.organizationId === org.id);
                return (
                  <Tr key={data.id}>
                    <Td>
                      <div>
                        <div className="flex gap-3 items-center">
                          <StyledLink
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              workspaceClick(data.workspaceId);
                            }}
                            to={routes.workspaceDetails.make(organization?.slug ?? '', data.workspace?.slug ?? '')}
                          >
                            {data.workspace.name} ({data.workspace.organization.name})
                          </StyledLink>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div>
                        <IconButtonDelete
                          aria-label="Unwatch workspace"
                          onClick={() => {
                            unwatchWorkspace(data.workspaceId);
                          }}
                          disabled={isRemovingSubscription}
                        >
                          <TrashBin />
                        </IconButtonDelete>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
      </DisclosureSection>
    );
  };

  const data: TData[] = [
    { type: 'jobAssigned', name: 'Job Assigned' },
    { type: 'repositoryAssigned', name: 'Repository Assigned' },
    { type: 'workspaceSubscriptions', name: 'Workspace Watch Subscriptions' },
    { type: 'editorSubscriptions', name: 'Editor Subscriptions' },
    { type: 'proofreaderSubscriptions', name: 'Proofreader Subscriptions' },
  ];

  return (
    <>
      <LinearProgressLoader active={isBusy} />
      <MainContent>
        {loadErrorBanner}
        <TableWrapper className="table">
          <Table>
            <Thead>
              <Tr>
                <Th>TYPE</Th>
                <Th>
                  <div className="items-center flex flex-col justify-center">
                    <div className="label">Email</div>
                    <CheckBox
                      name="email-notifications"
                      className="checkbox"
                      disabled={isUpdatingSettings}
                      checked={settings.emailNotifications === undefined ? false : settings.emailNotifications}
                      onChange={() => toggleSettings('emailNotifications')}
                    />
                  </div>
                </Th>
                <Th>
                  <div className="items-center flex flex-col justify-center">
                    <div className="label">Push</div>
                    <CheckBox
                      name="push-notifications"
                      className="checkbox"
                      disabled={isUpdatingSettings}
                      checked={settings.pushNotifications === undefined ? false : settings.pushNotifications}
                      onChange={() => toggleSettings('pushNotifications')}
                    />
                  </div>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item) => (
                <Tr key={item.type}>
                  <Td>
                    <p>{item.name}</p>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center">
                      <CheckBox
                        name={`email-notifications-${item.type}`}
                        className="switch"
                        disabled={isUpdatingSettings || !settings.emailNotifications}
                        checked={
                          settings[`${item.type}Email` as SettingsKey] === undefined
                            ? false
                            : settings[`${item.type}Email` as SettingsKey]
                        }
                        onChange={() => toggleSettings(`${item.type}Email` as SettingsKey)}
                      />
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center">
                      <CheckBox
                        name={`push-notifications-${item.type}`}
                        className="switch"
                        disabled={isUpdatingSettings || !settings.pushNotifications}
                        checked={
                          settings[`${item.type}Push` as SettingsKey] === undefined
                            ? false
                            : settings[`${item.type}Push` as SettingsKey]
                        }
                        onChange={() => toggleSettings(`${item.type}Push` as SettingsKey)}
                      />
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        <TableWrapper className="table">
          <Table>
            <Thead>
              <Tr>
                <Th>Job Watch Subscriptions</Th>
                <Th>
                  <div className="items-center flex flex-col justify-center">
                    <div className="label">Email</div>
                    <CheckBox
                      name="job-email-notifications"
                      className="checkbox"
                      disabled={isUpdatingSettings}
                      checked={
                        settings.jobSubscriptionsStatusEmail ||
                        settings.jobSubscriptionsAssigneeEmail ||
                        settings.jobSubscriptionsCommentsEmail
                      }
                      onChange={() => {
                        const value =
                          settings.jobSubscriptionsStatusEmail ||
                          settings.jobSubscriptionsAssigneeEmail ||
                          settings.jobSubscriptionsCommentsEmail;
                        updateSettings({
                          ...settings,
                          jobSubscriptionsStatusEmail: !value,
                          jobSubscriptionsAssigneeEmail: !value,
                          jobSubscriptionsCommentsEmail: !value,
                        });
                      }}
                    />
                  </div>
                </Th>
                <Th>
                  <div className="items-center flex flex-col justify-center">
                    <div className="label">Push</div>
                    <CheckBox
                      name="job-push-notifications"
                      className="checkbox"
                      disabled={isUpdatingSettings}
                      checked={
                        settings.jobSubscriptionsStatusPush ||
                        settings.jobSubscriptionsAssigneePush ||
                        settings.jobSubscriptionsCommentsPush
                      }
                      onChange={() => {
                        const value =
                          settings.jobSubscriptionsStatusPush ||
                          settings.jobSubscriptionsAssigneePush ||
                          settings.jobSubscriptionsCommentsPush;
                        updateSettings({
                          ...settings,
                          jobSubscriptionsStatusPush: !value,
                          jobSubscriptionsAssigneePush: !value,
                          jobSubscriptionsCommentsPush: !value,
                        });
                      }}
                    />
                  </div>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {[
                { type: 'jobSubscriptionsStatus', name: 'Status updates' },
                { type: 'jobSubscriptionsAssignee', name: 'Assignee updates' },
                { type: 'jobSubscriptionsComments', name: 'Comments' },
              ].map((item) => (
                <Tr key={item.type}>
                  <Td>
                    <p>{item.name}</p>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center">
                      <CheckBox
                        name={`job-email-notifications-${item.type}`}
                        className="switch"
                        disabled={isUpdatingSettings || !settings.emailNotifications}
                        checked={
                          settings[`${item.type}Email` as SettingsKey] === undefined
                            ? false
                            : settings[`${item.type}Email` as SettingsKey]
                        }
                        onChange={() => toggleSettings(`${item.type}Email` as SettingsKey)}
                      />
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-center">
                      <CheckBox
                        name={`job-push-notifications-${item.type}`}
                        className="switch"
                        disabled={isUpdatingSettings || !settings.pushNotifications}
                        checked={
                          settings[`${item.type}Push` as SettingsKey] === undefined
                            ? false
                            : settings[`${item.type}Push` as SettingsKey]
                        }
                        onChange={() => toggleSettings(`${item.type}Push` as SettingsKey)}
                      />
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        {renderJobsList()}
        {renderWorkspacesList()}
        {renderDevicesList()}
      </MainContent>
    </>
  );
};
